/**
 * WorkspaceUI.js
 *
 * Small shared pieces of the workspace dashboard: the label hook, the card shell,
 * the skeleton, the quiet state messages (empty / locked / error / "open in
 * admin") and the admin deep-link helper. Premium blueprint: solid surfaces
 * (never glass), soft border, brand orange accents, dark-mode aware.
 *
 * @module components/workspaces/WorkspaceUI
 */

import React, {useEffect, useMemo, useRef} from 'react';
import {Animated, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import {useManagementTheme} from '../../helpers/managementTheme';
import {ADMIN_HOST} from '../../helpers/deepLink/adminLinkResolver';
import {
    actionTitle,
    kpiLabel,
    label,
    resolveLabelLanguage,
    seriesLabel,
    termLabel,
    widgetTitle,
} from '../../helpers/workspaces/workspaceLabels';

/** Admin route of the global dashboard (target of "open in admin" for unsupported widgets). */
export const ADMIN_DASHBOARD_PATH = '/main/dashboard';

/**
 * Label helpers bound to the current app language.
 *
 * @returns {{language: string, title: Function, kpi: Function, series: Function, term: Function, action: Function, text: Function}} Helpers.
 */
export const useWorkspaceLabels = () => {
    const {i18n} = useTranslation();
    const language = resolveLabelLanguage(i18n.language);

    return useMemo(() => ({
        language,
        title: (type) => widgetTitle(language, type),
        kpi: (module, kpi) => kpiLabel(language, module, kpi),
        series: (module, series) => seriesLabel(language, module, series),
        term: (key, fallback) => termLabel(language, key, fallback),
        action: (item, formatAmount) => actionTitle(language, item, formatAmount),
        text: (key, fallback, params) => label(language, key, fallback, params),
    }), [language]);
};

/**
 * Open an admin web page inside the app (authenticated WebView), or do nothing for an empty path.
 *
 * @param {object} navigation React Navigation object.
 * @param {?string} path Admin route (`/crm/main`) or a full URL.
 * @returns {void}
 */
export const openInAdmin = (navigation, path) => {
    if (!path) {
        return;
    }

    const url = /^https?:\/\//i.test(path) ? path : `https://${ADMIN_HOST}${path.startsWith('/') ? path : `/${path}`}`;

    navigation.navigate('AdminWebScreen', {url});
};

/**
 * Solid widget card: header (icon chip + title + optional right slot) and a body.
 *
 * @param {object} props
 * @param {string} props.title Card title.
 * @param {string} [props.icon] MDI glyph of the header chip.
 * @param {string} [props.tone='primary'] Chip tone.
 * @param {React.ReactNode} [props.right] Right slot of the header.
 * @param {React.ReactNode} props.children Body.
 * @param {string|number} [props.width='100%'] Card width.
 * @param {() => void} [props.onPress] Makes the whole card tappable.
 * @returns {JSX.Element} The card.
 */
export const WidgetCard = ({title, icon, tone = 'primary', right, children, width = '100%', onPress}) => {
    const m = useManagementTheme();
    const Wrapper = onPress ? TouchableOpacity : View;

    return (
        <Wrapper
            {...(onPress ? {onPress, activeOpacity: 0.85, accessibilityRole: 'button'} : {})}
            style={[styles.card, {width, backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}
        >
            <View style={styles.cardHeader}>
                {!!icon && (
                    <View style={[styles.chip, {backgroundColor: m.toneTint(tone)}]}>
                        <Icon name={icon} size={16} color={m.toneColor(tone)} />
                    </View>
                )}
                <Text style={[styles.cardTitle, {color: m.colors.heading}]} numberOfLines={1}>{title}</Text>
                {right}
            </View>
            {children}
        </Wrapper>
    );
};

/**
 * Pulsing placeholder block.
 *
 * @param {object} props
 * @param {number} [props.height=14] Block height.
 * @param {string|number} [props.width='100%'] Block width.
 * @param {object} [props.style] Extra style.
 * @returns {JSX.Element} The block.
 */
export const SkeletonBlock = ({height = 14, width = '100%', style}) => {
    const m = useManagementTheme();
    const pulse = useRef(new Animated.Value(0.45)).current;

    useEffect(() => {
        const loop = Animated.loop(Animated.sequence([
            Animated.timing(pulse, {toValue: 1, duration: 750, useNativeDriver: true}),
            Animated.timing(pulse, {toValue: 0.45, duration: 750, useNativeDriver: true}),
        ]));

        loop.start();

        return () => loop.stop();
    }, [pulse]);

    return (
        <Animated.View
            style={[{height, width, borderRadius: 8, backgroundColor: m.colors.surfaceAlt, opacity: pulse}, style]}
        />
    );
};

/**
 * Loading placeholder shaped like a widget card.
 *
 * @param {{size?: string}} props `S` is half width.
 * @returns {JSX.Element} The skeleton card.
 */
export const WidgetSkeleton = ({size = 'M'}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.card, {width: '100%', backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
            <SkeletonBlock height={12} width="55%" />
            <SkeletonBlock height={size === 'S' ? 22 : 30} width="40%" style={styles.skelGap} />
            {size !== 'S' && <SkeletonBlock height={size === 'L' ? 92 : 56} style={styles.skelGap} />}
        </View>
    );
};

/**
 * Quiet in-card message (empty / locked / error / unsupported) with an optional pill action.
 *
 * @param {object} props
 * @param {string} props.icon MDI glyph.
 * @param {string} props.text Message.
 * @param {string} [props.actionLabel] Pill label.
 * @param {() => void} [props.onAction] Pill handler.
 * @param {string} [props.tone='neutral'] Icon tone.
 * @returns {JSX.Element} The message.
 */
export const WidgetMessage = ({icon, text, actionLabel, onAction, tone = 'neutral'}) => {
    const m = useManagementTheme();

    return (
        <View style={styles.message}>
            <Icon name={icon} size={22} color={m.toneColor(tone)} />
            <Text style={[styles.messageText, {color: m.colors.subText}]}>{text}</Text>
            {!!actionLabel && !!onAction && (
                <TouchableOpacity
                    onPress={onAction}
                    activeOpacity={0.8}
                    accessibilityRole="button"
                    style={[styles.pill, {borderColor: m.colors.primary}]}
                >
                    <Text style={[styles.pillText, {color: m.colors.primary}]}>{actionLabel}</Text>
                </TouchableOpacity>
            )}
        </View>
    );
};

/**
 * Tappable row used by the workspace sheets (icon chip + title + subtitle + trailing slot).
 *
 * @param {object} props
 * @param {string} props.icon MDI glyph.
 * @param {string} props.title Row title.
 * @param {string} [props.subtitle] Muted line.
 * @param {string} [props.tone='primary'] Chip tone.
 * @param {() => void} props.onPress Tap handler.
 * @param {React.ReactNode} [props.right] Trailing slot.
 * @param {boolean} [props.disabled=false] Dimmed, not tappable.
 * @returns {JSX.Element} The row.
 */
export const SheetRow = ({icon, title, subtitle, tone = 'primary', onPress, right, disabled = false}) => {
    const m = useManagementTheme();

    return (
        <TouchableOpacity
            onPress={onPress}
            disabled={disabled}
            activeOpacity={0.8}
            accessibilityRole="button"
            style={[styles.sheetRow, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border, opacity: disabled ? 0.5 : 1}]}
        >
            <View style={[styles.rowChip, {backgroundColor: m.toneTint(tone)}]}>
                <Icon name={icon} size={20} color={m.toneColor(tone)} />
            </View>
            <View style={styles.rowText}>
                <Text style={[styles.rowTitle, {color: m.colors.heading}]} numberOfLines={2}>{title}</Text>
                {!!subtitle && <Text style={[styles.rowSub, {color: m.colors.subText}]} numberOfLines={2}>{subtitle}</Text>}
            </View>
            {right}
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    sheetRow: {flexDirection: 'row', alignItems: 'center', borderRadius: 16, borderWidth: 1, padding: 12, marginBottom: 10},
    rowChip: {width: 40, height: 40, borderRadius: 14, alignItems: 'center', justifyContent: 'center', marginRight: 12},
    rowText: {flex: 1},
    rowTitle: {fontSize: 14.5, fontWeight: '700'},
    rowSub: {fontSize: 12.5, marginTop: 2},
    card: {borderRadius: 20, borderWidth: 1, padding: 14, marginBottom: 12},
    cardHeader: {flexDirection: 'row', alignItems: 'center', marginBottom: 10},
    chip: {width: 28, height: 28, borderRadius: 10, alignItems: 'center', justifyContent: 'center', marginRight: 8},
    cardTitle: {flex: 1, fontSize: 14, fontWeight: '700'},
    skelGap: {marginTop: 10},
    message: {alignItems: 'center', paddingVertical: 10, gap: 6},
    messageText: {fontSize: 13, textAlign: 'center'},
    pill: {marginTop: 4, borderWidth: 1.5, borderRadius: 999, paddingHorizontal: 14, paddingVertical: 6},
    pillText: {fontSize: 12.5, fontWeight: '700'},
});
